<template>
  <div class="print-only-container">
    <div class="receipt-container" id="printable-receipt">
    <div class="receipt-header text-center mb-20">
      <h2 class="fw-bold mb-1">{{ stateStore.siteName }}</h2>
      <p v-if="stateStore.receiptSettings.showAddress && stateStore.companyAddress" class="mb-0">
        {{ stateStore.companyAddress }}
      </p>
      <p v-if="stateStore.receiptSettings.showPhone && stateStore.companyPhone" class="mb-0">
        <!-- {{ $t("common.phone") }}: -->
        {{ stateStore.companyPhone }}
      </p>
      <p v-if="stateStore.receiptSettings.showEmail && stateStore.companyEmail" class="mb-0">
        {{ stateStore.companyEmail }}
      </p>
    </div>

    <div class="receipt-info mb-20">
      <div class="d-flex justify-content-between mb-1">
        <div><span class="fw-bold">{{ $t("sales_page.date") }}:</span></div>
        <div>{{ formatDate(sale.date || sale.created_at) }}</div>
      </div>
      <div class="d-flex justify-content-between mb-1" v-if="stateStore.receiptSettings.showCustomer">
        <span class="fw-bold">{{ $t("sales_page.customer") }}:</span> {{ sale.customer_name || sale.customer?.name || $t("sales_page.walk_in_customer") }}
      </div>
      <div class="d-flex justify-content-between">
        <div><span class="fw-bold">{{ $t("sales_page.reference_short") }}:</span></div>
        <div>{{ sale.reference_no }}</div>
      </div>
      <div class="d-flex justify-content-between">
        <div><span class="fw-bold">{{ $t("sales_page.warehouse") }}:</span></div>
        <div>{{ sale.warehouse_name || sale.warehouse?.name || $t("sales_page.not_available_short") }}</div>
      </div>
      <div v-if="stateStore.receiptSettings.showBarcode" class="text-center mt-2 small text-muted">
        {{ sale.reference_no }}
      </div>
    </div>

    <table class="table receipt-table mb-20">
      <thead>
        <tr>
          <th class="ps-0">
            <!-- {{ $t("sales_page.description") }} -->
            មុខទំនិញ <br> Descriptions
          </th>
          <th class="text-center">
            <!-- {{ $t("sales_page.qty") }} -->
            ចំនួន <br> Quantity
          </th>
          <th class="text-end">
            <!-- {{ $t("sales_page.price") }} -->
            តម្លៃ <br> Price
          </th>
          <th class="text-end pe-0">
            <!-- {{ $t("sales_page.total") }} -->
            តម្លៃសរុប <br> Total
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in sale.items" :key="item.id">
          <td class="ps-0">{{ item.product_name }}</td>
          <td class="text-center fw-bold">{{ item.quantity }}</td>
          <td class="text-end">{{ stateStore.formatPrice(Number(item.unit_price)) }}</td>
          <td class="text-end pe-0 fw-bold">{{ stateStore.formatPrice(Number(item.total)) }}</td>
        </tr>
      </tbody>
    </table>

    <div class="receipt-totals mb-20">
      <div class="d-flex justify-content-between mb-1 border-top pt-2">
        <span class="fw-bold">{{ $t("sales_page.subtotal") }}:</span>
        <span class="fw-bold">{{ stateStore.formatPrice(Number(sale.total)) }}</span>
      </div>
      <div
        v-if="stateStore.receiptSettings.showTaxDiscount && sale.discount && sale.discount > 0"
        class="d-flex justify-content-between mb-1"
      >
        <span class="fw-bold">{{ $t("sales_page.discount") }}:</span>
        <span class="fw-bold text-danger">-{{ sale.discount <= 100 && sale.discount_type === 'percentage' ? Number(sale.discount) + '%' : stateStore.formatPrice(Number(sale.discount)) }}</span>
      </div>
      <div
        v-if="stateStore.receiptSettings.showTaxDiscount && sale.coupon_discount && sale.coupon_discount > 0"
        class="d-flex justify-content-between mb-1"
      >
        <span class="fw-bold">{{ $t("sales_page.coupon") }}:</span>
        <span class="fw-bold text-danger">-{{ sale.coupon_discount <= 100 && sale.coupon_type === 'percentage' ? Number(sale.coupon_discount) + '%' : stateStore.formatPrice(Number(sale.coupon_discount)) }}</span>
      </div>
      <div
        v-if="stateStore.receiptSettings.showTaxDiscount && sale.tax && sale.tax > 0"
        class="d-flex justify-content-between mb-1"
      >
        <span class="fw-bold">{{ $t("sales_page.tax") }}:</span>
        <span class="fw-bold">{{ stateStore.formatPrice(Number(sale.tax)) }}</span>
      </div>
      <div
        v-if="stateStore.receiptSettings.showTaxDiscount && sale.shipping && sale.shipping > 0"
        class="d-flex justify-content-between mb-1"
      >
        <span class="fw-bold">{{ $t("sales_page.shipping") }}:</span>
        <span class="fw-bold">{{ stateStore.formatPrice(Number(sale.shipping)) }}</span>
      </div>
      <div class="d-flex justify-content-between mb-1 border-top pt-2">
        <span class="fw-bold fs-16">{{ $t("sales_page.grand_total") }}:</span>
        <span class="fw-bold fs-16">{{ stateStore.formatPrice(Number(sale.grand_total)) }}</span>
      </div>
      <div class="d-flex justify-content-between">
        <span class="fw-bold">{{ $t("sales_page.payment_method") }}:</span>
        <span class="fw-bold text-uppercase">{{ sale.paid_by || $t("sales_page.cash") }}</span>
      </div>
    </div>

    <div class="receipt-footer text-center mt-40 pt-20 border-top">
      <p v-if="stateStore.receiptSettings.showNoteToCustomer" class="mb-1 fw-bold">
        {{ stateStore.receiptSettings.noteToCustomer }}
      </p>
      <!-- <p class="text-muted small">{{ $t("sales_page.powered_by", { name: stateStore.siteName }) }}</p> -->
      <p class="text-muted small">Powered by ProCare Digital Marketing</p>
    </div>
  </div>
</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { Sale } from "../../../services/sales";
import stateStore from "@/utils/store";

export default defineComponent({
  name: "SaleReceipt",
  props: {
    sale: {
      type: Object as PropType<Sale>,
      required: true,
    },
  },
  setup() {
    const { locale } = useI18n();

    const formatDate = (dateStr: string) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    return {
      formatDate,
      stateStore,
    };
  },
});
</script>

<style scoped>
.receipt-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
  background: #fff;
  font-family: 'Arial', sans-serif;
  color: #000;
}

.receipt-table {
  width: 100%;
  border-collapse: collapse;
}

.receipt-table th {
  border-bottom: 2px solid #000;
  padding: 8px 0;
  font-weight: bold;
  text-transform: capitalize;
}

.receipt-table td {
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.border-top {
  border-top: 2px solid #000 !important;
}

.fs-16 {
  font-size: 16px;
}

.mt-40 {
  margin-top: 40px;
}

.pt-20 {
  padding-top: 20px;
}

@media print {
  body {
    background: white !important;
  }
  
  #printable-receipt {
    display: block !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 20px !important;
  }

  @page {
    margin: 0;
    size: auto;
  }
}

/* Helper styles for normal view */
.print-only-container {
  display: block;
}
</style>
